import { EditingLeaseState, type EditingLeaseRead } from '../schemas'
import { isHeldByAnotherUser } from './is-held-by-another-user'

export enum EditingLeasePhase {
  INITIAL = 'initial',
  EDITING = 'editing',
  WAITING = 'waiting',
  REFRESHING = 'refreshing',
}

type EditingLeaseSnapshot = {
  lease: EditingLeaseRead | null
  enabled: boolean
  phase: EditingLeasePhase
  pending: boolean
  // Monotonic local deadline derived from the server's remaining lease lifetime.
  deadline: number | null
}
export type EditingLeaseEvent =
  | { type: 'configure'; enabled: boolean }
  | { type: 'acquiring' }
  | { type: 'received'; lease: EditingLeaseRead; now: number; enabled?: boolean; confirmed?: boolean; beforeEntry?: boolean }
  | { type: 'conflict'; lease: EditingLeaseRead; now: number; enabled: boolean }
  | { type: 'expired' }
  | { type: 'failed' }
  | { type: 'settled' }

export function editingLeaseReducer(state: EditingLeaseSnapshot, event: EditingLeaseEvent): EditingLeaseSnapshot {
  switch (event.type) {
    case 'configure':
      return { ...state, enabled: event.enabled, phase: state.phase === EditingLeasePhase.EDITING ? EditingLeasePhase.INITIAL : state.phase }
    case 'acquiring':
      return { ...state, pending: true, phase: state.phase === EditingLeasePhase.EDITING ? EditingLeasePhase.WAITING : state.phase }
    case 'received': {
      const enabled = event.enabled ?? state.enabled
      const needsRefresh = state.phase === EditingLeasePhase.WAITING || state.phase === EditingLeasePhase.REFRESHING
      let phase = state.phase
      if (enabled && !(event.beforeEntry && phase === EditingLeasePhase.INITIAL)) {
        if (!event.lease.is_current_user_holder) phase = EditingLeasePhase.WAITING
        else if (needsRefresh && !event.confirmed) phase = EditingLeasePhase.REFRESHING
        else phase = EditingLeasePhase.EDITING
      }
      return {
        ...state,
        enabled,
        phase,
        lease: event.lease,
        deadline: event.lease.expires_at ? event.now + Math.max(0, Date.parse(event.lease.expires_at) - Date.parse(event.lease.server_now)) : null,
      }
    }
    case 'conflict':
      return editingLeaseReducer({ ...state, phase: EditingLeasePhase.WAITING, pending: false }, { ...event, type: 'received' })
    case 'expired':
      return {
        ...state,
        phase: EditingLeasePhase.WAITING,
        deadline: null,
        lease: state.lease
          ? { ...state.lease, state: EditingLeaseState.AVAILABLE, is_current_user_holder: false, holder_display_name: null, expires_at: null }
          : null,
      }
    case 'failed':
      // A failed heartbeat does not revoke confirmed ownership; its deadline still applies.
      return {
        ...state,
        phase: state.enabled && state.phase !== EditingLeasePhase.EDITING ? EditingLeasePhase.WAITING : state.phase,
      }
    case 'settled':
      return { ...state, pending: false }
  }
}

export function canWriteWithLease(state: EditingLeaseSnapshot): boolean {
  // OFF status reads are advisory. Only a known other holder blocks ordinary permissions.
  return state.enabled ? state.phase === EditingLeasePhase.EDITING && state.lease?.is_current_user_holder === true : !isHeldByAnotherUser(state.lease)
}
